import { Box, Typography, Button, CircularProgress, IconButton } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { IoChevronBack, IoCamera, IoCloudUpload } from 'react-icons/io5';
import { useMagicLinkAppProvider, MagicLinkView, getToken } from '@/Provider/MagicLinkAppProvider';
import { ID_CARD_ASPECT_RATIO, cropPhotoTopBottom, compressPhoto, dataURLtoFile } from '@/Utils/MagicLink/idCardUtil';
import { useIdCardUpload, type UploadIdCardOutput } from 'request-form-lib';
import { uploadIdCardWithValidation } from '@/Api/MagicLinkApi';

const CameraView = () => {
    const { navigateToView, magicLinkData, setFrontSideUploadResult, setBackSideUploadResult, setValidationErrorMessage, getNextSideToCapture } = useMagicLinkAppProvider();
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [hasPermission, setHasPermission] = useState(false);
    const [permissionDenied, setPermissionDenied] = useState(false);
    const [isVideoReady, setIsVideoReady] = useState(false);
    const [capturedImage, setCapturedImage] = useState<string | null>(null);
    const [isCropping, setIsCropping] = useState(false);
    const [countdown, setCountdown] = useState<number | null>(null);
    const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // Get requestId from magic link data
    const requestId = magicLinkData?.linkData?.requestId;

    // Get token hash for upload
    const linkTokenHash = getToken();

    // Determine which side to capture
    const sideToCapture = getNextSideToCapture();
    const isCapturingFront = sideToCapture === 'front';
    const isCapturingBack = sideToCapture === 'back';

    // Use centralized upload hook
    // Only initialize hook if requestId is available, otherwise it will be recreated when requestId becomes available
    const { isUploading, uploadFiles } = useIdCardUpload({
        requestId: requestId || '',
        linkTokenHash: linkTokenHash!,
        uploadService: uploadIdCardWithValidation,
        onUploadSuccess: (result: UploadIdCardOutput) => {
            // Store result in appropriate state based on isBackSide flag
            if (result.isBackSide === true) {
                setBackSideUploadResult(result);
            } else {
                setFrontSideUploadResult(result);
            }
            navigateToView(MagicLinkView.EXTRACTED_DATA);
        },
        onUploadError: (error: string) => {
            setValidationErrorMessage(error);
            navigateToView(MagicLinkView.INVALID_ID_CARD);
        },
        onUploadComplete: () => {
            // Upload completed
        },
    });

    const startCamera = async () => {
        try {
            setIsLoading(true);
            setError(null);
            setPermissionDenied(false);
            setIsVideoReady(false);

            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: 'environment', // Use back camera on mobile
                    aspectRatio: ID_CARD_ASPECT_RATIO,
                    width: { ideal: 1920 }, // Request high resolution
                    height: { ideal: 1080 }, // Request high resolution
                },
            });

            if (videoRef.current) {
                const video = videoRef.current;
                video.srcObject = stream;
                streamRef.current = stream;

                // Wait for video to be ready (especially important for iOS)
                const handleLoadedMetadata = () => {
                    // Explicitly play the video (required for iOS Safari)
                    video.play()
                        .then(() => {
                            setHasPermission(true);
                            setPermissionDenied(false);
                            setIsVideoReady(true);
                            setIsLoading(false);
                        })
                        .catch((playError) => {
                            console.error('Error playing video:', playError);
                            setError('Nepodarilo sa spustiť kameru. Skúste to znova.');
                            setHasPermission(false);
                            setPermissionDenied(true);
                            setIsLoading(false);
                        });
                };

                const handleError = () => {
                    console.error('Video element error');
                    setError('Nepodarilo sa načítať video stream.');
                    setHasPermission(false);
                    setPermissionDenied(true);
                    setIsLoading(false);
                };

                // Remove old listeners if they exist
                video.removeEventListener('loadedmetadata', handleLoadedMetadata);
                video.removeEventListener('error', handleError);

                // Add new listeners
                video.addEventListener('loadedmetadata', handleLoadedMetadata, { once: true });
                video.addEventListener('error', handleError, { once: true });

                // If metadata is already loaded, trigger manually
                if (video.readyState >= 1) {
                    handleLoadedMetadata();
                }
            }
        } catch (err) {
            console.error('Error accessing camera:', err);
            setError('Nepodarilo sa pristúpiť ku kamere. Prosím, skontrolujte povolenia.');
            setHasPermission(false);
            setPermissionDenied(true);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        startCamera();

        // Cleanup: stop camera when component unmounts
        return () => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
            }
            if (videoRef.current) {
                videoRef.current.srcObject = null;
            }
            if (countdownIntervalRef.current) {
                clearInterval(countdownIntervalRef.current);
            }
        };
    }, []);

    const startCountdown = () => {
        // Don't start countdown if already counting down or processing
        if (countdown !== null || isCropping) {
            return;
        }

        setCountdown(3);

        // Countdown timer
        countdownIntervalRef.current = setInterval(() => {
            setCountdown((prev) => {
                if (prev === null) {
                    return null;
                }
                if (prev <= 1) {
                    // Clear interval first
                    if (countdownIntervalRef.current) {
                        clearInterval(countdownIntervalRef.current);
                        countdownIntervalRef.current = null;
                    }
                    // Countdown finished, capture the image after a brief delay
                    setTimeout(() => {
                        setCountdown(null);
                        handleCapture();
                    }, 100);
                    return null;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const handleCapture = async () => {
        if (videoRef.current && canvasRef.current) {
            const video = videoRef.current;
            const canvas = canvasRef.current;
            const context = canvas.getContext('2d', {
                willReadFrequently: false, // Better for quality
            });

            if (context) {
                // Enable high-quality image smoothing
                context.imageSmoothingEnabled = true;
                context.imageSmoothingQuality = 'high';

                // Set canvas size to match video (use actual video dimensions)
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;

                // Draw video frame to canvas
                context.drawImage(video, 0, 0, canvas.width, canvas.height);

                // Get image data with maximum quality (1.0 = 100% quality)
                const imageData = canvas.toDataURL('image/jpeg', 1.0);

                // Stop the video stream
                if (streamRef.current) {
                    streamRef.current.getTracks().forEach(track => track.stop());
                }

                // Crop and compress the image before showing preview
                setIsCropping(true);
                try {
                    const deviceWidth = window.innerWidth;
                    const deviceHeight = window.innerHeight;

                    // First crop the image
                    const croppedImage = await cropPhotoTopBottom(imageData, deviceWidth, deviceHeight);

                    // Then compress the cropped image
                    const compressedImage = await compressPhoto(croppedImage);

                    // Display the compressed cropped image
                    setCapturedImage(compressedImage);

                    // Check if requestId is available before uploading
                    const currentRequestId = magicLinkData?.linkData?.requestId;
                    if (!currentRequestId) {
                        setError('Request ID nie je k dispozícii. Skúste to znova.');
                        setIsCropping(false);
                        return;
                    }

                    // Convert to File and upload
                    const file = dataURLtoFile(compressedImage, 'id-card.jpg');
                    await uploadFiles([file]);
                } catch (error) {
                    console.error('Error processing image:', error);
                    // If processing fails, try to upload original image
                    try {
                        setCapturedImage(imageData);

                        // Check if requestId is available before uploading
                        const currentRequestId = magicLinkData?.linkData?.requestId;
                        if (!currentRequestId) {
                            setError('Request ID nie je k dispozícii. Skúste to znova.');
                            setIsCropping(false);
                            return;
                        }

                        const file = dataURLtoFile(imageData, 'id-card.jpg');
                        await uploadFiles([file]);
                    } catch (uploadError) {
                        setError('Nepodarilo sa nahrať obrázok. Skúste to znova.');
                        setIsCropping(false);
                    }
                } finally {
                    setIsCropping(false);
                }
            }
        }
    };


    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                setError('Prosím, vyberte obrázok.');
                return;
            }

            // Read file and convert to data URL
            const reader = new FileReader();
            reader.onload = async (e) => {
                const imageData = e.target?.result as string;

                // Crop and compress the image before showing preview
                setIsCropping(true);
                try {
                    const deviceWidth = window.innerWidth;
                    const deviceHeight = window.innerHeight;

                    // First crop the image
                    const croppedImage = await cropPhotoTopBottom(imageData, deviceWidth, deviceHeight);

                    // Then compress the cropped image
                    const compressedImage = await compressPhoto(croppedImage);

                    // Display the compressed cropped image
                    setCapturedImage(compressedImage);

                    // Check if requestId is available before uploading
                    const currentRequestId = magicLinkData?.linkData?.requestId;
                    if (!currentRequestId) {
                        setError('Request ID nie je k dispozícii. Skúste to znova.');
                        setIsCropping(false);
                        return;
                    }

                    // Convert to File and upload
                    const fileToUpload = dataURLtoFile(compressedImage, file.name || 'id-card.jpg');
                    await uploadFiles([fileToUpload]);
                } catch (error) {
                    console.error('Error processing image:', error);
                    // If processing fails, try to upload original image
                    try {
                        setCapturedImage(imageData);

                        // Check if requestId is available before uploading
                        const currentRequestId = magicLinkData?.linkData?.requestId;
                        if (!currentRequestId) {
                            setError('Request ID nie je k dispozícii. Skúste to znova.');
                            setIsCropping(false);
                            return;
                        }

                        const fileToUpload = dataURLtoFile(imageData, file.name || 'id-card.jpg');
                        await uploadFiles([fileToUpload]);
                    } catch (uploadError) {
                        console.error('Error uploading image:', uploadError);
                        setError('Nepodarilo sa nahrať obrázok. Skúste to znova.');
                        setIsCropping(false);
                    }
                } finally {
                    setIsCropping(false);
                }
            };
            reader.readAsDataURL(file);

            // Reset file input
            event.target.value = '';
        }
    };

    return (
        <Box
            sx={{
                height: '100vh',
                width: '100%',
                position: 'relative',
                backgroundColor: 'black',
                overflow: 'hidden',
            }}
        >
            {/* Full Screen Video Background - Always render for iOS compatibility */}
            <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    zIndex: hasPermission && isVideoReady && !capturedImage ? 1 : -1,
                    opacity: hasPermission && isVideoReady && !capturedImage ? 1 : 0,
                }}
            />

            {/* Single processing/upload overlay - only one loader for crop + upload */}
            {(isCropping || isUploading) && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'black',
                        zIndex: 2000,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                    }}
                >
                    <CircularProgress sx={{ color: 'white' }} />
                    <Typography variant="body2" sx={{ color: 'white' }}>
                        {isUploading ? 'Nahrávanie ID karty...' : 'Spracovávam obrázok...'}
                    </Typography>
                </Box>
            )}

            {/* Back Button - Top Left */}
            <IconButton
                onClick={() => {
                    // If capturing back side, go to front side extracted data view
                    // Otherwise go to intro
                    if (isCapturingBack) {
                        navigateToView(MagicLinkView.EXTRACTED_DATA);
                    } else {
                        navigateToView(MagicLinkView.INTRO);
                    }
                }}
                sx={{
                    position: 'absolute',
                    top: 16,
                    left: 16,
                    zIndex: 1200,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    color: 'white',
                    '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    },
                }}
            >
                <IoChevronBack size={24} />
            </IconButton>

            {isLoading && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2,
                        zIndex: 1000,
                    }}
                >
                    <CircularProgress sx={{ color: 'white' }} />
                    <Typography variant="body2" sx={{ color: 'white' }}>
                        Načítavam kameru...
                    </Typography>
                </Box>
            )}

            {/* Permission Denied / No Permission UI */}
            {(permissionDenied || !hasPermission) && !isLoading && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 3,
                        padding: 3,
                        textAlign: 'center',
                        zIndex: 1000,
                        width: '90%',
                        maxWidth: 400,
                    }}
                >
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 500 }}>
                        Prístup ku kamere
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'white', opacity: 0.9 }}>
                        {error || 'Pre pokračovanie potrebujeme prístup ku kamere. Môžete udeľ povolenie alebo nahrať obrázok manuálne.'}
                    </Typography>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            width: '100%',
                        }}
                    >
                        <Button
                            variant="contained"
                            startIcon={<IoCamera size={20} />}
                            onClick={startCamera}
                            sx={{
                                textTransform: 'none',
                                height: '50px',
                                fontWeight: 500,
                            }}
                        >
                            Povoliť prístup ku kamere
                        </Button>

                        <Button
                            variant="outlined"
                            startIcon={<IoCloudUpload size={20} />}
                            onClick={() => fileInputRef.current?.click()}
                            sx={{
                                textTransform: 'none',
                                height: '50px',
                                fontWeight: 500,
                                borderColor: 'white',
                                color: 'white',
                                '&:hover': {
                                    borderColor: 'rgba(255, 255, 255, 0.8)',
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                },
                            }}
                        >
                            Nahrať obrázok manuálne
                        </Button>
                    </Box>

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileUpload}
                        style={{ display: 'none' }}
                    />
                </Box>
            )}

            {hasPermission && isVideoReady && !isLoading && !capturedImage && (
                <>
                    {/* Headline above frame */}
                    <Typography
                        variant="h6"
                        sx={{
                            position: 'absolute',
                            top: 80,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            color: 'white',
                            textShadow: '0 2px 4px rgba(0, 0, 0, 0.9)',
                            textAlign: 'center',
                            zIndex: 1200,
                            paddingX: 2,
                            fontWeight: 500,
                        }}
                    >
                        {isCapturingFront ? 'Predná strana občianskeho preukazu' : 'Zadná strana občianskeho preukazu'}
                    </Typography>

                    {/* ID Card Frame Overlay - 85.60 × 53.98 mm ratio - Centered */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '90%',
                            maxWidth: '600px',
                            aspectRatio: ID_CARD_ASPECT_RATIO,
                            border: '3px solid',
                            borderColor: 'white',
                            borderRadius: '8px',
                            pointerEvents: 'none',
                            zIndex: 1100,
                            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.6)',
                        }}
                    />

                    {/* Instructions below frame */}
                    <Typography
                        variant="body2"
                        sx={{
                            position: 'absolute',
                            top: 'calc(50% + 22vh)',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            color: 'white',
                            textShadow: '0 2px 4px rgba(0, 0, 0, 0.9)',
                            textAlign: 'center',
                            zIndex: 1100,
                            paddingX: 2,
                        }}
                    >
                        {isCapturingFront ? 'Umiestnite prednú stranu dokladu do rámčeku' : 'Umiestnite zadnú stranu dokladu do rámčeku'}
                    </Typography>

                    {/* Circular Capture Button - Bottom Center */}
                    <Button
                        variant={countdown !== null ? "outlined" : "contained"}
                        onClick={startCountdown}
                        disabled={countdown !== null || isCropping}
                        sx={{
                            position: 'absolute',
                            bottom: 40,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            borderRadius: '50%',
                            width: 70,
                            height: 70,
                            minWidth: 70,
                            padding: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: countdown !== null ? 'transparent' : 'white',
                            color: 'white',
                            border: countdown !== null ? '4px solid white' : 'none',
                            zIndex: 1200,
                            '&:hover': {
                                backgroundColor: countdown !== null ? 'transparent' : 'rgba(255, 255, 255, 0.9)',
                            },
                            '&:disabled': {
                                backgroundColor: 'transparent',
                                border: '4px solid white',
                            },
                            boxShadow: countdown !== null ? 'none' : '0 4px 12px rgba(0, 0, 0, 0.5)',
                        }}
                    >
                        {countdown !== null && (
                            <Typography
                                variant="h4"
                                sx={{
                                    color: 'white',
                                    fontWeight: 'bold',
                                    fontSize: '2rem',
                                    lineHeight: 1,
                                    margin: 0,
                                    marginBottom: "4px",
                                    padding: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                {countdown}
                            </Typography>
                        )}
                    </Button>
                </>
            )}

            {/* Hidden canvas for capturing */}
            <canvas ref={canvasRef} style={{ display: 'none' }} />
        </Box>
    );
};

export default CameraView;

